import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

export type InfoDockMode = 'details' | 'history' | 'comments'

type DocumentInfoDockProps = {
  mode: InfoDockMode
  onModeChange: (mode: InfoDockMode) => void
  onClose: () => void
  /** When false, only Details content is shown (e.g. Basecamp edit panes). */
  showHistoryTab?: boolean
  /** Drive preview: comments live in this dock, not a third column. */
  showCommentsTab?: boolean
  children: React.ReactNode
  /** Optional actions shown next to close (e.g. compare hint in History mode). */
  headerActions?: React.ReactNode
}

/**
 * Shared right-rail chrome for document Details + History.
 * One dock toggle (Info); mode switch lives inside the panel.
 */
export function DocumentInfoDock({
  mode,
  onModeChange,
  onClose,
  showHistoryTab = true,
  showCommentsTab = false,
  children,
  headerActions,
}: DocumentInfoDockProps) {
  return (
    <div
      className="flex h-full min-w-0 max-w-full flex-col overflow-hidden bg-card"
      data-testid={testIds.documentInfoDock.root}
    >
      <div className="flex h-11 shrink-0 items-center justify-between gap-2 border-b border-border px-4">
        <h3 className="truncate text-sm font-semibold text-card-foreground">Document Info</h3>
        <div className="flex shrink-0 items-center gap-1">
          {headerActions}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={onClose}
            aria-label="Close document info"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {showHistoryTab || showCommentsTab ? (
        <div className="shrink-0 border-b border-border px-4 py-2">
          <div
            className="flex gap-1 rounded-md bg-muted/50 p-0.5"
            role="tablist"
            aria-label="Document info sections"
            data-testid={testIds.documentInfoDock.modeTabs}
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'details'}
              data-testid={testIds.documentInfoDock.detailsTab}
              onClick={() => onModeChange('details')}
              className={cn(
                'flex-1 rounded-sm px-2 py-1 text-xs font-medium transition-colors',
                mode === 'details'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Details
            </button>
            {showHistoryTab ? (
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'history'}
                data-testid={testIds.documentViewer.versionHistoryToggle}
                onClick={() => onModeChange('history')}
                className={cn(
                  'flex-1 rounded-sm px-2 py-1 text-xs font-medium transition-colors',
                  mode === 'history'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                History
              </button>
            ) : null}
            {showCommentsTab ? (
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'comments'}
                data-testid={testIds.documentInfoDock.commentsTab}
                onClick={() => onModeChange('comments')}
                className={cn(
                  'flex-1 rounded-sm px-2 py-1 text-xs font-medium transition-colors',
                  mode === 'comments'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Comments
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  )
}
